import { pbiMockDataEnabledAtom } from "../../state/pbi.atoms";
import { log, logE, logV } from "../../../shared/services/debug-logger";
import { TF_HULL_EVALUATION_MOCKS } from "../mocks/tf-hull-evaluations.mocks";
import { TfHullEvaluationsResponse } from "../models/tf-hull-evaluations.models";
import { fetchTfHullEvaluations } from "../services/tf-hull-evaluations.service";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";

// Owns loading and source switching so the page only renders prepared dashboard state.
export function useTfHullEvaluations(enabled: boolean) {
  const mockEnabled = useAtomValue(pbiMockDataEnabledAtom);
  const [response, setResponse] = useState<TfHullEvaluationsResponse>();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const latestRequest = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled) {
      logV("STATE - refresh - disabled");
      return;
    }

    const requestId = latestRequest.current + 1;
    log("STATE - refresh - TF hull evaluations", { mock: mockEnabled });
    latestRequest.current = requestId;
    setLoading(true);
    setError("");
    setResponse(undefined);

    try {
      const nextResponse = mockEnabled
        ? {
            vessels: TF_HULL_EVALUATION_MOCKS,
            totalCount: TF_HULL_EVALUATION_MOCKS.length,
            lastRefreshedAt: new Date().toISOString(),
          }
        : await fetchTfHullEvaluations();

      if (latestRequest.current === requestId) {
        log("STATE - refresh - TF hull evaluations apply", { count: nextResponse.vessels.length });
        setResponse(nextResponse);
      }
    } catch {
      if (latestRequest.current === requestId) {
        logE(new Error("STATE - refresh - TF hull evaluations failed"));
        setResponse(undefined);
        setError("The live Snowflake dashboard data could not be loaded.");
      }
    } finally {
      if (latestRequest.current === requestId) {
        setLoading(false);
      }
    }
  }, [enabled, mockEnabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);
  useEffect(
    () => () => {
      latestRequest.current += 1;
    },
    [],
  );

  return { response, error, loading, mockEnabled, refresh };
}
